import { FC, type ReactNode } from "react";
import { twMerge } from "tailwind-merge";

import { SortDirection } from "mds/components/SortableTable";
import { Tooltip } from "mds/components/Tooltip";
import { ArrowDownIcon, ArrowUpDownIcon, ArrowUpIcon } from "mds/icons";

type THProps = {
  className?: string;
  children: ReactNode;
  isVertical?: boolean;
  verticalDivClassName?: string;
  verticalDivTitle?: string;
};
export const TH: FC<THProps> = ({
  className,
  isVertical,
  verticalDivClassName,
  verticalDivTitle,
  children,
}) => (
  <th
    className={twMerge(
      "sticky top-0 py-2 pr-4 text-left font-medium text-black-tint-20",
      isVertical ? "w-8 content-end" : "whitespace-nowrap",
      className,
    )}
  >
    {isVertical ? (
      <div
        className={twMerge(
          "max-h-32 max-w-[80px] rotate-180 content-end overflow-hidden text-ellipsis whitespace-nowrap pb-2 pl-2 pt-2 text-left [writing-mode:vertical-lr]",
          verticalDivClassName,
        )}
        title={verticalDivTitle}
      >
        {children}
      </div>
    ) : (
      children
    )}
  </th>
);

type TDProps = { className?: string; title?: string; children: ReactNode };
export const TD: FC<TDProps> = ({ className, title, children }) => (
  <td className={twMerge("whitespace-nowrap py-1.5 pr-4", className)} title={title}>
    {children}
  </td>
);

type THeadProps = { className?: string; children: ReactNode };
export const THead: FC<THeadProps> = ({ className, children }) => (
  <thead className={twMerge("border-b border-black-tint-70", className)}>{children}</thead>
);

type TableProps = { className?: string; children: ReactNode };
export const Table: FC<TableProps> = ({ className, children }) => (
  <table className={twMerge("w-full whitespace-nowrap text-xs", className)}>{children}</table>
);

type TRProps = { className?: string; children: ReactNode };
export const TR: FC<TRProps> = ({ className, children }) => (
  <tr className={twMerge("border-b border-black-tint-90", className)}>{children}</tr>
);

type SortableTHProps = {
  className?: string;
  field: string;
  label: string;
  tooltip?: string;
  activeField: string | null;
  sortDirection: SortDirection;
  onSort: (field: string) => void;
};

export const SortableTH: FC<SortableTHProps> = ({
  className,
  field,
  label,
  tooltip,
  activeField,
  sortDirection,
  onSort,
}) => {
  const icon =
    activeField !== field ? (
      <ArrowUpDownIcon size="xs" />
    ) : sortDirection === "asc" ? (
      <ArrowUpIcon size="xs" />
    ) : (
      <ArrowDownIcon size="xs" />
    );

  return (
    <TH className={className}>
      <div
        className="flex w-full cursor-pointer items-center gap-1 hover:text-blue"
        onClick={() => onSort(field)}
      >
        {tooltip ? <Tooltip text={tooltip}>{label}</Tooltip> : label}
        {icon}
      </div>
    </TH>
  );
};
